<template>
  <div v-if="isShow" class="banner" :class="className">
    <image-slider :images="images"></image-slider>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import ImageSlider from '@/components/point/ImageSlider.vue'

/**
 * ImageSlider.vueに渡す画像
 * 
 * 例)
 * [{ 'url': (URL), 'title': (タイトル) }, ...]
 */
let images = ref([])

/**
 * バナーを表示する
 * true: 表示
 */
const isShow = computed(() => images.value.length)

/**
 * バナーのクラス
 * 画像の枚数に応じてクラスを設定する
 */
const className = computed(() => isShow.value ? `length_${images.value.length}` : '')

/**
 * バナー用のマップの追加フィールド
 * storelocator.extra_fieldsをグループごとに再編成した構造
 * 
 * 例)
 * {
 *   "banner_1": {
 *     "url": "https://commit.carlifesquare.com/",
 *     "title": "コスモのコミっと車検",
 *     ...
 *   },
 *   ...
 * }
 */
const extraFields = computed(() => {
  let extraFields: {
    [key: string]: any
  } = {}

  if (storelocator.extra_fields) {
    for (const [groupAndKey, value] of Object.entries(storelocator.extra_fields)) {
      const group = groupAndKey.split('.')[0]
      const key = groupAndKey.split('.')[1]
  
      // バナーのフィールド以外は除外
      if (!/^banner_(?:[1-9]|10)$/.test(group)) {
        continue
      }
  
      if (group in extraFields) {
        extraFields[group][key] = value
      } else {
        extraFields[group] = { [key]: value }
      }
    }
  }

  return extraFields
})

onMounted(() => {
  /**
   * 日時
   */
  type DateTime = {
    date: string,
    hour: string,
    minute: string
  }

  /**
   * 日時の日、時、分を結合して返す
   * @param dateTime 日時
   * @returns 結合した文字列　2025-01-020304
   */
  const generateDateTimeString = (dateTime: DateTime) => {
    return `${dateTime.date}${dateTime.hour}${dateTime.minute}`
  }

  /**
   * 現在時刻をyyyy-mm-ddhhmm形式の文字列で取得する
   */
  const getCurrentDateTimeString = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0') // 月は0から始まるため+1
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    return `${year}-${month}-${day}${hours}${minutes}`
  }

  /**
   * 日時の日、時、分の全てが空でないことを確認する
   */
  const dateTimeIsValid = (dateTime: DateTime) => {
    return dateTime.date !== '' && dateTime.hour !== '' && dateTime.minute !== ''
  }

  images.value = Object.values(extraFields.value).filter(fields => {
    /**
     * 絞り込み(以下を全て満たす場合に表示)
     * ・画像がある
     * ・表示フラグが立っている
     * ・日時フィールドが空ではない
     * ・開始日＜終了日
     * ・掲載期間内
     */
    const now = getCurrentDateTimeString()
    return  fields.image?.url && storelocator.point.point.extra_fields[fields.flag_field] === '1'
      && dateTimeIsValid(fields.created_at) && dateTimeIsValid(fields.pub_start) && dateTimeIsValid(fields.pub_end)
      && generateDateTimeString(fields.pub_start) <= now && now < generateDateTimeString(fields.pub_end)
  }).sort((a, b) => {
    // 登録日時の降順
    if (generateDateTimeString(a.created_at) > generateDateTimeString(b.created_at)) {
      return -1
    }
    if (generateDateTimeString(a.created_at) < generateDateTimeString(b.created_at)) {
      return 1
    }
    return 0
  }).map(fields => ({
    url: fields.image.url,
    link: fields.url,
    alt: fields.title,
    bottomText: fields.title,
  }))
})
</script>

<style lang="scss" scoped>
  .banner {
    margin-top: 1rem;
  }
</style>
